import dayjs, { Dayjs } from "dayjs";
import type DecimalType from "decimal.js";

import { Allergen, CatLifeStage } from "@/primary-models";

import {
  ADULT_CAT_AGE_YEARS,
  BODY_TYPE_CALORIE_MULTIPLIER,
  BREEDS,
  CALORIE_THRESHOLD,
  CALORIES_PER_KG_PER_DAY,
  GROWTH_CURVE,
  KITTEN_CALORIE_THRESHOLD,
  KITTEN_GROWTH_COEFFICIENT,
  KITTEN_GROWTH_EQUATION_OFFSET,
  KITTEN_MAINTENANCE_CALORIES_PER_KG,
  KITTEN_MATURITY_RATIO_COEFFICIENT,
  LIFESTYLE_CALORIE_MULTIPLIER,
  MEAL_PLAN_CALORIES,
  METABOLIC_WEIGHT_EXPONENT,
  NEUTERED_CALORIE_MULTIPLIER,
  RETAIL_PLAN_PORTION,
  SENIOR_CAT_AGE_YEARS,
  UNIVERSAL_ALLERGENS,
  WEEKS_PER_MONTH,
} from "./cat-calculations.constants";
import {
  BodyType,
  Breed,
  Gender,
  ICatAge,
  ICatProfile,
  IEstimatedWeights,
  IFraction,
  IMealPlan,
  IRetailTrayPortion,
  LifeStyle,
  Portion,
  PouchSize,
  TDecimalRounding,
} from "./cat-calculations.types";

/**
 * This is the single source of truth for cat validation ranges.
 */
export const CAT_VALIDATION = {
  MAX_NAME_LENGTH: 250,
  MIN_WEIGHT_KG: 0.1,
  MAX_WEIGHT_KG: 25,

  // Legacy/Combined mode (when both weeks + months provided)
  MIN_AGE_WEEKS_COMBINED: 0,
  MAX_AGE_WEEKS_COMBINED: 5,
  MIN_AGE_MONTHS_COMBINED: 0,
  MAX_AGE_MONTHS_COMBINED: 11,

  // Standalone weeks mode (new flow)
  MIN_AGE_WEEKS: 8,
  MAX_AGE_WEEKS: 51,

  // Standalone months mode (new flow)
  MIN_AGE_MONTHS: 2,
  MAX_AGE_MONTHS: 11,

  MIN_AGE_YEARS: 1,
  MAX_AGE_YEARS: 30,

  MIN_CATS_IN_HOUSEHOLD: 1,
  MAX_CATS_IN_HOUSEHOLD: 4,
} as const;

const ZERO_WEIGHTS: IEstimatedWeights = { lower: 0, estimated: 0, upper: 0 };

export const range = (
  start: number,
  stop: number,
) => Array.from({ length: stop - start + 1 }, (_value, index) => start + index);

export const isKitten = (age: ICatAge) => {
  if (age.type && [ "adult", "senior" ].includes(age.type)) { return false; }
  if (age.years && age.years >= 1) { return false; }
  if (age.months && age.months >= 12) { return false; }
  if (age.weeks && age.weeks >= 52) { return false; }

  return true;
};

/** What percentage of their adult weight a kitten is expected to have reached. */
export const getGrowthCurvePercentage = (ageInWeeks: number): number =>
  GROWTH_CURVE[Number((ageInWeeks / 2).toFixed(0))];

/** Not rounded to whole weeks: the growth curve reads more accurately from the exact age. */
export const getExactAgeInWeeks = (age: ICatAge): number =>
  (Number(age.months) || 0) * WEEKS_PER_MONTH + (Number(age.weeks) || 0);

/** A kitten's expected weight based on their age and 'just right' adult weight. */
export const getEstimatedKittenWeightKg = (
  justRightAdultWeightKg: number,
  ageInWeeks: number,
): number => Number((justRightAdultWeightKg * getGrowthCurvePercentage(ageInWeeks) / 100).toFixed(1));

/**
 * A cat's expected weight range based on their breed, gender and body type.
 * Shown to the customer in weight inputs which they can manually edit.
 */
export const calculateCatWeight = (
  breed: Breed,
  age: ICatAge,
  gender: Gender,
  bodyType: BodyType = "UNKNOWN",
): IEstimatedWeights => {
  const breedDetails = BREEDS.find((b) => b.value === breed)
    ?? BREEDS.find((b) => b.value === "I_DONT_KNOW_THEIR_BREED");

  if (!breed || !age || !gender || !breedDetails) {
    return ZERO_WEIGHTS;
  }

  const profile = gender === "BOY"
    ? breedDetails.maleWeights
    : breedDetails.femaleWeights;

  // Kitten weight estimate isn't based on body type as they are rarely over/underweight.
  if (age.type === "kitten") {
    const entry = profile.JUST_RIGHT;
    const ageInWeeks = getExactAgeInWeeks(age);

    return {
      lower: getEstimatedKittenWeightKg(entry.lower, ageInWeeks),
      estimated: getEstimatedKittenWeightKg(entry.estimated, ageInWeeks),
      upper: getEstimatedKittenWeightKg(entry.upper, ageInWeeks),
    };
  }

  return profile[bodyType];
};

/**
 * The 'just right' adult weight for a specific breed and gender.
 * Used as a fallback for calculating kitten calories.
 */
export const getHealthyAdultWeightKg = (
  breed: Breed | null | undefined,
  gender: Gender | null | undefined,
): number => calculateCatWeight(
  breed ?? "I_DONT_KNOW_THEIR_BREED",
  { type: "adult" },
  gender ?? "UNKNOWN",
  "JUST_RIGHT",
).estimated;

/**
 * Determines the target adult weight for kitten calorie calculations.
 *
 * Under 14 weeks, body type is ignored (kittens this young are rarely over/underweight)
 * and the target is always projected from the kitten's current weight via the growth curve.
 *
 * From 15 weeks, overweight kittens use the healthy adult weight for their breed/gender to avoid overfeeding.
 * All other body types continue to derive the target from current weight, treating it as a frame-size indicator.
 */
export const getKittenTargetAdultWeightKg = (
  weightInKg: number,
  ageInWeeks: number,
  healthyAdultWeightKg: number,
  bodyType: BodyType,
): number => {
  const isOverweight = bodyType === "OVERWEIGHT" || bodyType === "VERY_OVERWEIGHT";

  if (!isOverweight || ageInWeeks <= 14) {
    const growthPercentage = getGrowthCurvePercentage(ageInWeeks);
    if (growthPercentage > 0) {
      return Number((weightInKg / (growthPercentage / 100)).toFixed(1));
    }
  }
  return healthyAdultWeightKg;
};

export const calculateSuggestedCaloriesForKitten = (
  weightInKg: number,
  ageInWeeks: number,
  targetAdultWeightKg: number,
): number => {
  if (targetAdultWeightKg <= 0) {
    return 0;
  }

  // Under 14 weeks, we use a growth curve equation based on the kitten's current weight.
  if (range(1, 14).includes(ageInWeeks)) {
    return Math.floor(
      100 *
      weightInKg ** METABOLIC_WEIGHT_EXPONENT *
      KITTEN_GROWTH_COEFFICIENT *
      (Math.E ** (KITTEN_MATURITY_RATIO_COEFFICIENT * (weightInKg / targetAdultWeightKg))
      - KITTEN_GROWTH_EQUATION_OFFSET),
    );
  }

  // From 15 weeks, we apply a multiplier to the target adult weight based the number of weeks old.
  if (range(15, 40).includes(ageInWeeks)) {
    return Math.floor(1.75 * KITTEN_MAINTENANCE_CALORIES_PER_KG * targetAdultWeightKg);
  }
  if (range(41, 48).includes(ageInWeeks)) {
    return Math.floor(1.5 * KITTEN_MAINTENANCE_CALORIES_PER_KG * targetAdultWeightKg);
  }
  return Math.floor(1.25 * KITTEN_MAINTENANCE_CALORIES_PER_KG * targetAdultWeightKg);
};

export const calculateSuggestedCaloriesForKittenByAgeObject = (
  weightInKg: number,
  age: ICatAge,
  healthyAdultWeightKg: number,
  bodyType: BodyType,
): number => {
  const exactAgeInWeeks = getExactAgeInWeeks(age);
  const ageInWeeks = Math.round(exactAgeInWeeks);

  const targetAdultWeightKg = getKittenTargetAdultWeightKg(
    weightInKg,
    ageInWeeks,
    healthyAdultWeightKg,
    bodyType,
  );

  return calculateSuggestedCaloriesForKitten(weightInKg, ageInWeeks, targetAdultWeightKg);
};

export const calculateSuggestedCaloriesForGrownCat = (
  weightInKg: number,
  bodyType: BodyType,
  lifestyle: LifeStyle,
  isNeutered?: boolean | null,
): number => {
  const idealBodyWeight = weightInKg * BODY_TYPE_CALORIE_MULTIPLIER[bodyType];
  const calories =
    idealBodyWeight ** METABOLIC_WEIGHT_EXPONENT *
    CALORIES_PER_KG_PER_DAY *
    LIFESTYLE_CALORIE_MULTIPLIER[lifestyle] *
    (isNeutered === false
      ? NEUTERED_CALORIE_MULTIPLIER.INTACT
      : NEUTERED_CALORIE_MULTIPLIER.NEUTERED);

  return Math.round(calories * 10) / 10;
};

export const calculateSuggestedCaloriesFromBirthday = (
  weight: number,
  bodyType: BodyType,
  lifestyle: LifeStyle,
  birthday: Dayjs,
  catProfile: ICatProfile,
  isNeutered?: boolean | null,
): number => {
  if (dayjs().diff(birthday, "years") < 1) {
    const ageInWeeks = dayjs().diff(birthday, "weeks");
    const healthyAdultWeightKg = getHealthyAdultWeightKg(catProfile.breed, catProfile.gender);
    const targetAdultWeightKg = getKittenTargetAdultWeightKg(
      weight,
      ageInWeeks,
      healthyAdultWeightKg,
      bodyType,
    );

    return calculateSuggestedCaloriesForKitten(weight, ageInWeeks, targetAdultWeightKg);
  }
  return calculateSuggestedCaloriesForGrownCat(weight, bodyType, lifestyle, isNeutered);
};

export const calculateSuggestedCalories = (
  weightInKg: number,
  bodyType: BodyType,
  lifestyle: LifeStyle,
  age: ICatAge,
  catProfile: ICatProfile,
  isNeutered?: boolean | null,
): number => {
  if (isKitten(age)) {
    return calculateSuggestedCaloriesForKittenByAgeObject(
      weightInKg,
      age,
      getHealthyAdultWeightKg(catProfile.breed, catProfile.gender),
      bodyType,
    );
  }
  return calculateSuggestedCaloriesForGrownCat(weightInKg, bodyType, lifestyle, isNeutered);
};

export const calculateTraySize = (
  caloriesNeeded: number,
  calorieThreshold: number = CALORIE_THRESHOLD,
): IMealPlan => {
  const trays = Object.entries(MEAL_PLAN_CALORIES);

  for (const [ traySize, trayCalories ] of trays) {
    if (caloriesNeeded <= trayCalories + calorieThreshold) {
      return {
        size: traySize as PouchSize,
        calories: trayCalories,
      };
    }
  }
  const [ size, calories ]: [string, number] = trays[trays.length - 1];

  return {
    size: size as PouchSize,
    calories,
  };
};

export const calculateRetailTrayServings = (
  caloriesNeeded: number,
  calorieThreshold: number = CALORIE_THRESHOLD,
): IRetailTrayPortion => {
  const trays = Object.entries(RETAIL_PLAN_PORTION);

  for (const [ trayCalories, trayPortion ] of trays) {
    if (caloriesNeeded <= +trayCalories + calorieThreshold) {
      return {
        portion: trayPortion as Portion,
        calories: +trayCalories,
      };
    }
  }
  const [ calories, portion ]: [string, number] = trays[trays.length - 1];

  return {
    portion: portion as Portion,
    calories: +calories,
  };
};

export const calculateSuggestedCatTraySize = (
  weight: number,
  bodyType: BodyType,
  lifestyle: LifeStyle,
  age: ICatAge,
  catProfile: ICatProfile,
  isNeutered?: boolean | null,
): IMealPlan => {
  const suggestedCalories = calculateSuggestedCalories(
    weight,
    bodyType,
    lifestyle,
    age,
    catProfile,
    isNeutered,
  );
  if (isKitten(age)) {
    return calculateTraySize(suggestedCalories, KITTEN_CALORIE_THRESHOLD);
  }
  return calculateTraySize(suggestedCalories);
};

const getGreatestCommonDivisor = (a: number, b: number): number => (a % b ? getGreatestCommonDivisor(b, a % b) : b);

export const makeFractionFromDecimal = (
  decimal: number,
  denominator: number,
  rounding: TDecimalRounding = "UP",
): IFraction => {
  const factor: number = 100 / denominator;
  const numeratorTotal = (decimal * 100) / factor;
  const wholeNumber = Math.floor(numeratorTotal / denominator);
  let decimalPlaces = numeratorTotal / denominator - wholeNumber;

  if (rounding === "UP") {
    decimalPlaces = Math.ceil(decimalPlaces / (factor / 100)) * (factor / 100);
  } else {
    decimalPlaces = Math.floor(decimalPlaces / (factor / 100)) * (factor / 100);
  }
  const numerator = (decimalPlaces * 100) / factor;

  const gcd = getGreatestCommonDivisor(numerator, denominator);
  const numeratorAdjustedForLowestCommonDenominator = numerator / gcd;
  const denominatorAdjustedForLowestCommonDenominator = denominator / gcd;

  return {
    wholeNumber: wholeNumber > 0 ? wholeNumber : undefined,
    fraction:
      numeratorAdjustedForLowestCommonDenominator > 0
        ? {
          numerator: numeratorAdjustedForLowestCommonDenominator,
          denominator: denominatorAdjustedForLowestCommonDenominator,
        }
        : undefined,
  };
};

export function getCatLifeStage(birthday: dayjs.ConfigType): CatLifeStage {

  if (!birthday) {
    // If the cat has no birthday, we assume it's an adult.
    return "ADULT";
  }

  const age = dayjs().diff(birthday, "year");

  if (age < 0) {
    return "ADULT";
  }

  if (age < ADULT_CAT_AGE_YEARS[0]) {
    return "KITTEN";
  }

  if (age < SENIOR_CAT_AGE_YEARS[0]) {
    return "ADULT";
  }

  return "SENIOR";
}

export const breedHairMap = new Map(
  BREEDS.map((b) => [ b.value, b.isLongHaired ] as const),
);

export const isLongHairedBreed = (breed: Breed) => breedHairMap.get(breed) ?? false;

const DAYS_PER_MONTH = 28;

export const getCompetitorMonthlyPriceDiff =
  (katkinPrice: number, competitorPrice: number) => +(
    katkinPrice * DAYS_PER_MONTH - (competitorPrice || 0) * DAYS_PER_MONTH).toFixed();

export const isCatFullyAllergic = (allergens?: Allergen[]): boolean => {
  if (!allergens || allergens.length === 0) { return false; }

  const allergenSet = new Set(allergens);
  return UNIVERSAL_ALLERGENS.some((allergenGroup) => allergenGroup.every((allergen) => allergenSet.has(allergen)));
};

/** Converts a number of grams to kilograms with a maximum of 1 decimal place.
 * So, for example, 1234 grams would be converted to 1.2 kilograms.
 * @return The converted number or NaN if the input was invalid. */
export function gramsToKgs(grams: number | string | DecimalType): number {
  return Math.round(Number(grams) / 100) / 10;
}

/** Converts a number of kilograms to grams with a maximum of 0 decimal places.
 * So, for example, 1.2345 kilograms would be converted to 1235 grams.
 * @return The converted number or NaN if the input was invalid. */
export function kgsToGrams(kgs: number | string | DecimalType): number {
  return Math.round(Number(kgs) * 1000);
}

function validateAge(age: number | undefined, max: number, min: number): number | null {
  if (!age) { return null; }
  return age <= max && age >= min ? age : null;
}

export function getAgeAtSignup(
  cat: {
    catLifeStage?: CatLifeStage;
    catAgeMonths?: number;
    catAgeWeeks?: number;
    catAgeYears?: number;
    ageMode?: "months" | "weeks" | "years";
  },
): {
  years?: number | null;
  months?: number | null;
  weeks?: number | null;
} | undefined {
  if (cat.catLifeStage === CatLifeStage.KITTEN) {

    if (cat.ageMode === "months" && cat.catAgeMonths) {
      return {
        months: validateAge(
          cat.catAgeMonths,
          CAT_VALIDATION.MAX_AGE_MONTHS,
          CAT_VALIDATION.MIN_AGE_MONTHS,
        ),
      };
    }
    if (cat.ageMode === "weeks" && cat.catAgeWeeks) {
      return {
        weeks: validateAge(
          cat.catAgeWeeks,
          CAT_VALIDATION.MAX_AGE_WEEKS,
          CAT_VALIDATION.MIN_AGE_WEEKS,
        ),
      };
    }

    if (cat.catAgeMonths || cat.catAgeWeeks) {
      return {
        months: validateAge(
          cat.catAgeMonths,
          CAT_VALIDATION.MAX_AGE_MONTHS_COMBINED,
          CAT_VALIDATION.MIN_AGE_MONTHS_COMBINED,
        ),
        weeks: validateAge(
          cat.catAgeWeeks,
          CAT_VALIDATION.MAX_AGE_WEEKS_COMBINED,
          CAT_VALIDATION.MIN_AGE_WEEKS_COMBINED,
        ),
      };
    }
    return undefined;
  }

  if (cat.catAgeYears) {
    return {
      years: cat.catAgeYears,
    };
  }
  return undefined;

}
